/**
 * Pure TEA update for UI zoom percent.
 *
 * Shell: `useUiZoom.ts` (CSS var + localPrefs + shortcut debounce via
 * `dispatchInto`).
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";

export const UI_ZOOM_DEFAULT_PERCENT = 100;
export const UI_ZOOM_MIN_PERCENT = 80;
export const UI_ZOOM_MAX_PERCENT = 150;
export const UI_ZOOM_STEP = 10;

export function clampUiZoomPercent(value: number): number {
  const stepped = Math.round(value / UI_ZOOM_STEP) * UI_ZOOM_STEP;
  return Math.min(UI_ZOOM_MAX_PERCENT, Math.max(UI_ZOOM_MIN_PERCENT, stepped));
}

export type UiZoomModel = {
  readonly percent: number;
};

export function initialUiZoomModel(percent: number): UiZoomModel {
  return { percent: clampUiZoomPercent(percent) };
}

export type UiZoomMsg =
  | { readonly type: "SetPercent"; readonly percent: number }
  | { readonly type: "ZoomIn" }
  | { readonly type: "ZoomOut" }
  | { readonly type: "Reset" };

export type UiZoomCmd = {
  readonly kind: "applyPercent";
  readonly percent: number;
};

export function updateUiZoom(
  model: UiZoomModel,
  msg: UiZoomMsg,
): readonly [UiZoomModel, readonly UiZoomCmd[]] {
  switch (msg.type) {
    case "SetPercent": {
      const percent = clampUiZoomPercent(msg.percent);
      if (percent === model.percent) {
        return [model, [{ kind: "applyPercent", percent }]];
      }
      return [{ percent }, [{ kind: "applyPercent", percent }]];
    }
    case "ZoomIn": {
      const percent = clampUiZoomPercent(model.percent + UI_ZOOM_STEP);
      return [{ percent }, [{ kind: "applyPercent", percent }]];
    }
    case "ZoomOut": {
      const percent = clampUiZoomPercent(model.percent - UI_ZOOM_STEP);
      return [{ percent }, [{ kind: "applyPercent", percent }]];
    }
    case "Reset":
      return [
        { percent: UI_ZOOM_DEFAULT_PERCENT },
        [{ kind: "applyPercent", percent: UI_ZOOM_DEFAULT_PERCENT }],
      ];
    default:
      return assertNever(msg);
  }
}

export const updateUiZoomFn: UpdateFn<UiZoomModel, UiZoomMsg, UiZoomCmd> =
  updateUiZoom;
